import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, getDocs, collection, updateDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useUser } from '../../context/UserContext';
import Swal from 'sweetalert2';
import UserNav from '../../components/navbar/user-nav';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import Breadcrumb from '../../components/Breadcrumb';

export default function EditarAsociado() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useUser();

  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);

  // Datos del asociado
  const [asociado, setAsociado] = useState<any>({
    nombre: '',
    correo: '',
    telefono: '',
    biografia: '',
    años_experiencia: 0,
    especialidad: '',
    licencia: '',
    inmobiliaria_doc_id: '',
    redes: {
      facebook: '',
      instagram: '',
      twitter: '',
      linkedin: ''
    },
    sitio_web: '',
    activo: true
  });

  // Lista de inmobiliarias disponibles
  const [inmobiliarias, setInmobiliarias] = useState<any[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      // Validar acceso
      if (!user || !user.tipo?.includes('admin')) {
        Swal.fire({
          icon: 'error',
          title: 'Acceso denegado',
          text: 'No tienes permiso para acceder a esta sección.'
        });
        navigate('/admin-dashboard');
        return;
      }

      if (!id) {
        Swal.fire({
          icon: 'error',
          title: 'ID no válido',
          text: 'No se proporcionó un ID de asociado válido.'
        });
        navigate('/admin-asociado');
        return;
      }

      try {
        // Cargar datos del asociado
        const asociadoDocRef = doc(db, 'asociado', id);
        const asociadoSnap = await getDoc(asociadoDocRef);

        if (!asociadoSnap.exists()) {
          Swal.fire({
            icon: 'error',
            title: 'No encontrado',
            text: 'El asociado no existe.'
          });
          navigate('/admin-asociado');
          return;
        }

        const asociadoData = asociadoSnap.data();

        // Establecer datos del asociado
        setAsociado({
          nombre: asociadoData.nombre || '',
          correo: asociadoData.correo || '',
          telefono: asociadoData.telefono || '',
          biografia: asociadoData.biografia || '',
          años_experiencia: asociadoData.años_experiencia || 0,
          especialidad: asociadoData.especialidad || '',
          licencia: asociadoData.licencia || '',
          inmobiliaria_doc_id: asociadoData.inmobiliaria_doc_id || '',
          redes: {
            facebook: asociadoData.redes?.facebook || '',
            instagram: asociadoData.redes?.instagram || '',
            twitter: asociadoData.redes?.twitter || '',
            linkedin: asociadoData.redes?.linkedin || ''
          },
          sitio_web: asociadoData.sitio_web || '',
          activo: asociadoData.activo !== false
        });

        // Cargar todas las inmobiliarias disponibles
        const inmobiliariasSnap = await getDocs(collection(db, 'inmobiliaria'));
        const inmoList = inmobiliariasSnap.docs.map(d => ({
          id: d.id,
          nombre: d.data().nombre || 'Sin nombre'
        }));
        setInmobiliarias(inmoList);

      } catch (err) {
        console.error('Error al cargar asociado:', err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Ocurrió un error al cargar los datos del asociado.'
        });
        navigate('/admin-asociado');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, id, navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (name.startsWith('redes.')) {
      const redName = name.split('.')[1];
      setAsociado((prev: any) => ({
        ...prev,
        redes: {
          ...prev.redes,
          [redName]: value
        }
      }));
    } else {
      setAsociado((prev: any) => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setGuardando(true);

    try {
      // Validaciones básicas
      if (!asociado.nombre.trim()) {
        Swal.fire({
          icon: 'warning',
          title: 'Nombre requerido',
          text: 'El nombre del asociado es obligatorio.'
        });
        setGuardando(false);
        return;
      }

      if (!asociado.correo.trim()) {
        Swal.fire({
          icon: 'warning',
          title: 'Correo requerido',
          text: 'El correo del asociado es obligatorio.'
        });
        setGuardando(false);
        return;
      }

      // Preparar datos para guardar
      const dataToSave: any = {
        nombre: asociado.nombre.trim(),
        correo: asociado.correo.trim(),
        telefono: asociado.telefono.trim(),
        biografia: asociado.biografia.trim(),
        años_experiencia: parseInt(asociado.años_experiencia) || 0,
        especialidad: asociado.especialidad.trim(),
        licencia: asociado.licencia.trim(),
        inmobiliaria_doc_id: asociado.inmobiliaria_doc_id || '',
        redes: {
          facebook: asociado.redes.facebook.trim(),
          instagram: asociado.redes.instagram.trim(),
          twitter: asociado.redes.twitter.trim(),
          linkedin: asociado.redes.linkedin.trim()
        },
        sitio_web: asociado.sitio_web.trim(),
        activo: asociado.activo
      };

      // Actualizar en Firestore
      const asociadoDocRef = doc(db, 'asociado', id!);
      await updateDoc(asociadoDocRef, dataToSave);

      Swal.fire({
        icon: 'success',
        title: 'Guardado exitoso',
        text: 'Los datos del asociado se actualizaron correctamente.'
      });

      navigate(`/ver-admin-asociado/${id}`);
    } catch (err) {
      console.error('Error al guardar asociado:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo guardar los cambios. Intenta nuevamente.'
      });
    } finally {
      setGuardando(false);
    }
  };

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
        <p className="mt-3 text-muted">Cargando información del asociado...</p>
      </div>
    );
  }

  if (!user || !user.tipo?.includes('admin')) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">
          <h4>Acceso Denegado</h4>
          <p>No tienes permiso para acceder a esta sección</p>
          <button className="btn btn-primary" onClick={() => navigate('/admin-dashboard')}>
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <UserNav />

      <div className="page-title">
        <div className="container">
          <h2 className="ipt-title">Editar Asociado</h2>
          <span className="ipn-subtitle">{asociado.nombre}</span>
        </div>
      </div>

      <section className="gray-simple">
        <div className="container">
          <Breadcrumb
            items={[
              { label: 'Dashboard', path: '/admin-dashboard' },
              { label: 'Administración de Asociados', path: '/admin-asociado' },
              { label: asociado.nombre, path: `/ver-admin-asociado/${id}` },
              { label: 'Editar' }
            ]}
          />

          <form onSubmit={handleSubmit}>
            {/* Información Básica */}
            <div className="card mb-4">
              <div className="card-header">
                <h5><i className="fas fa-user me-2"></i>Información Básica</h5>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label htmlFor="nombre" className="form-label">Nombre completo *</label>
                    <input
                      type="text"
                      className="form-control"
                      id="nombre"
                      name="nombre"
                      value={asociado.nombre}
                      onChange={handleInputChange}
                      placeholder="Nombre completo del asociado"
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="correo" className="form-label">Correo electrónico *</label>
                    <input
                      type="email"
                      className="form-control"
                      id="correo"
                      name="correo"
                      value={asociado.correo}
                      onChange={handleInputChange}
                      placeholder="correo@ejemplo.com"
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="telefono" className="form-label">Teléfono</label>
                    <input
                      type="tel"
                      className="form-control"
                      id="telefono"
                      name="telefono"
                      value={asociado.telefono}
                      onChange={handleInputChange}
                      placeholder="Número de teléfono"
                    />
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="años_experiencia" className="form-label">Años de experiencia</label>
                    <input
                      type="number"
                      className="form-control"
                      id="años_experiencia"
                      name="años_experiencia"
                      value={asociado.años_experiencia}
                      onChange={handleInputChange}
                      min="0"
                      placeholder="0"
                    />
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="especialidad" className="form-label">Especialidad</label>
                    <input
                      type="text"
                      className="form-control"
                      id="especialidad"
                      name="especialidad"
                      value={asociado.especialidad}
                      onChange={handleInputChange}
                      placeholder="Especialidad del asociado"
                    />
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="licencia" className="form-label">Licencia</label>
                    <input
                      type="text"
                      className="form-control"
                      id="licencia"
                      name="licencia"
                      value={asociado.licencia}
                      onChange={handleInputChange}
                      placeholder="Número de licencia"
                    />
                  </div>

                  <div className="col-12">
                    <label htmlFor="biografia" className="form-label">Biografía</label>
                    <textarea
                      className="form-control"
                      id="biografia"
                      name="biografia"
                      rows={4}
                      value={asociado.biografia}
                      onChange={handleInputChange}
                      placeholder="Biografía del asociado"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Inmobiliaria */}
            <div className="card mb-4">
              <div className="card-header">
                <h5><i className="fas fa-building me-2"></i>Inmobiliaria</h5>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-12">
                    <label htmlFor="inmobiliaria_doc_id" className="form-label">Asignar a Inmobiliaria *</label>
                    <select
                      className="form-select"
                      id="inmobiliaria_doc_id"
                      name="inmobiliaria_doc_id"
                      value={asociado.inmobiliaria_doc_id}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Seleccionar inmobiliaria</option>
                      {inmobiliarias.map(inmo => (
                        <option key={inmo.id} value={inmo.id}>
                          {inmo.nombre}
                        </option>
                      ))}
                    </select>
                    <small className="text-muted">
                      Los asociados pertenecen a una sola inmobiliaria
                    </small>
                  </div>
                </div>
              </div>
            </div>

            {/* Redes Sociales */}
            <div className="card mb-4">
              <div className="card-header">
                <h5><i className="fas fa-share-alt me-2"></i>Redes Sociales y Sitio Web</h5>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label htmlFor="redes.facebook" className="form-label">
                      <i className="fab fa-facebook text-primary me-2"></i>Facebook
                    </label>
                    <input
                      type="url"
                      className="form-control"
                      id="redes.facebook"
                      name="redes.facebook"
                      value={asociado.redes.facebook}
                      onChange={handleInputChange}
                      placeholder="https://facebook.com/..."
                    />
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="redes.instagram" className="form-label">
                      <i className="fab fa-instagram text-danger me-2"></i>Instagram
                    </label>
                    <input
                      type="url"
                      className="form-control"
                      id="redes.instagram"
                      name="redes.instagram"
                      value={asociado.redes.instagram}
                      onChange={handleInputChange}
                      placeholder="https://instagram.com/..."
                    />
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="redes.twitter" className="form-label">
                      <i className="fab fa-twitter text-info me-2"></i>Twitter
                    </label>
                    <input
                      type="url"
                      className="form-control"
                      id="redes.twitter"
                      name="redes.twitter"
                      value={asociado.redes.twitter}
                      onChange={handleInputChange}
                      placeholder="https://twitter.com/..."
                    />
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="redes.linkedin" className="form-label">
                      <i className="fab fa-linkedin text-primary me-2"></i>LinkedIn
                    </label>
                    <input
                      type="url"
                      className="form-control"
                      id="redes.linkedin"
                      name="redes.linkedin"
                      value={asociado.redes.linkedin}
                      onChange={handleInputChange}
                      placeholder="https://linkedin.com/in/..."
                    />
                  </div>

                  <div className="col-md-12">
                    <label htmlFor="sitio_web" className="form-label">
                      <i className="fas fa-globe text-secondary me-2"></i>Sitio Web
                    </label>
                    <input
                      type="url"
                      className="form-control"
                      id="sitio_web"
                      name="sitio_web"
                      value={asociado.sitio_web}
                      onChange={handleInputChange}
                      placeholder="https://..."
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Botones de acción */}
            <div className="card mb-4">
              <div className="card-body">
                <div className="d-flex justify-content-between">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => navigate(`/ver-admin-asociado/${id}`)}
                    disabled={guardando}
                  >
                    <i className="fas fa-times me-2"></i>
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={guardando}
                  >
                    {guardando ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Guardando...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save me-2"></i>
                        Guardar Cambios
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
